'use client';
import type {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {faBuilding} from '@fortawesome/pro-solid-svg-icons/faBuilding';
import {useEffect, useState} from 'react';
import {AppIcon, type AppIconProps} from './AppIcon';

type IconPack = Record<string, IconDefinition | undefined>;

// The backend amenity/feature contract sends an arbitrary FA icon name (e.g.
// "faPersonSwimming"), so the whole solid pack (~1 MB) plus brands must be
// available at runtime. They are loaded on demand into their own chunk instead of
// being bundled into every page: the home page never needs them.
let packs: [IconPack, IconPack] | null = null;
let packsPromise: Promise<[IconPack, IconPack]> | null = null;

export function loadFaIconPacks(): Promise<[IconPack, IconPack]> {
  packsPromise ??= Promise.all([
    import('@fortawesome/pro-solid-svg-icons').then(module => module.fas as IconPack),
    import('@fortawesome/free-brands-svg-icons').then(module => module.fab as IconPack),
  ]).then(loaded => {
    packs = loaded;
    return loaded;
  });
  return packsPromise;
}

export function resolveFaIcon(name: string, [solid, brands]: [IconPack, IconPack]): IconDefinition {
  const icon = solid[name] ?? brands[name];
  if (process.env.NODE_ENV !== 'production' && !icon) {
    console.warn(`resolveFaIcon: no FontAwesome icon named "${name}"`);
  }
  return icon ?? faBuilding;
}

export type NamedFaIconProps = Omit<AppIconProps, 'icon'> & {name: string};

export function NamedFaIcon({name, style, ...rest}: NamedFaIconProps) {
  const [icon, setIcon] = useState<IconDefinition | null>(() => (packs ? resolveFaIcon(name, packs) : null));

  useEffect(() => {
    let active = true;
    void loadFaIconPacks().then(loaded => {
      if (active) {
        setIcon(resolveFaIcon(name, loaded));
      }
    });
    return () => {
      active = false;
    };
  }, [name]);

  return <AppIcon icon={icon ?? faBuilding} style={icon ? style : {...style, visibility: 'hidden'}} {...rest} />;
}

NamedFaIcon.displayName = 'NamedFaIcon';
